import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { deleteField } from 'firebase/firestore'
import { getEffectiveDayPlan, formatDayDate, TOTAL_DAYS } from '../data/plan'
import { useDayLog, useInventory, useAllLogs, useAiPlan, useCustomMenus } from '../lib/hooks'
import { useDailyAlerts, requestNotificationPermission, notificationsSupported } from '../lib/notifications'
import { fileToCompressedDataUrl } from '../lib/image'
import { getEffectiveMeals, getEffectiveIngredients, getEffectiveGroceryList, getMealEditableItems } from '../lib/mealOverrides'
import { getExercisePRs, formatExerciseLog } from '../lib/exerciseStats'
import { getExerciseLogType } from '../data/workouts'
import CheckRow from '../components/CheckRow'
import Marquee from '../components/Marquee'
import LoadingScreen from '../components/LoadingScreen'
import MealEditor from '../components/MealEditor'
import ExerciseHistoryChart from '../components/ExerciseHistoryChart'
import CustomMenuBar from '../components/CustomMenuBar'

export default function DayDetail({ dayNumber, isToday }) {
  const { aiPlan } = useAiPlan()
  const plan = getEffectiveDayPlan(dayNumber, aiPlan)
  const { log, updateLog } = useDayLog(dayNumber)
  const { inventory, adjustStock } = useInventory()
  const { logs: allLogs } = useAllLogs()
  const { menus: customMenus, saveMenu: saveCustomMenu, deleteMenu: deleteCustomMenu } = useCustomMenus()
  const tomorrowDayNumber = dayNumber + 1
  const hasTomorrow = isToday && tomorrowDayNumber <= TOTAL_DAYS
  const { log: tomorrowLog } = useDayLog(hasTomorrow ? tomorrowDayNumber : null)
  const [weightInput, setWeightInput] = useState('')
  const [stepsInput, setStepsInput] = useState('')
  const [notesInput, setNotesInput] = useState('')
  const [waistInput, setWaistInput] = useState('')
  const [chestInput, setChestInput] = useState('')
  const [armsInput, setArmsInput] = useState('')
  const [thighsInput, setThighsInput] = useState('')
  const [measurementsOpen, setMeasurementsOpen] = useState(false)
  const [alertsOn, setAlertsOn] = useState(() => localStorage.getItem('alertsEnabled') === 'true')
  const [photoBusy, setPhotoBusy] = useState(false)
  const [editingMealKey, setEditingMealKey] = useState(null)
  const [exerciseInputs, setExerciseInputs] = useState({})
  const [historyExerciseName, setHistoryExerciseName] = useState(null)

  useDailyAlerts(isToday ? plan : null, isToday && alertsOn)

  useEffect(() => {
    if (log?.weightKg != null) setWeightInput(String(log.weightKg))
    if (log?.steps != null) setStepsInput(String(log.steps))
    if (log?.notes != null) setNotesInput(log.notes)
    if (log?.waistCm != null) setWaistInput(String(log.waistCm))
    if (log?.chestCm != null) setChestInput(String(log.chestCm))
    if (log?.armsCm != null) setArmsInput(String(log.armsCm))
    if (log?.thighsCm != null) setThighsInput(String(log.thighsCm))
  }, [log?.weightKg, log?.steps, log?.notes, log?.waistCm, log?.chestCm, log?.armsCm, log?.thighsCm, dayNumber])

  useEffect(() => {
    const next = {}
    for (const e of plan.exercises) {
      const entry = log?.exerciseLogs?.[e.name]
      next[e.name] = {
        weight: entry?.weight != null ? String(entry.weight) : '',
        reps: entry?.reps != null ? String(entry.reps) : '',
        duration: entry?.duration != null ? String(entry.duration) : '',
        rounds: entry?.rounds != null ? String(entry.rounds) : '',
      }
    }
    setExerciseInputs(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayNumber, log?.exerciseLogs])

  if (!log) return <LoadingScreen message={`Loading day ${dayNumber}`} />

  const effectiveMeals = getEffectiveMeals(plan.meals, log.customMeals)
  const exercisePRs = getExercisePRs(allLogs)

  const toggleExercise = (name) => {
    const next = { ...(log.exerciseChecks || {}), [name]: !log.exerciseChecks?.[name] }
    updateLog({ exerciseChecks: next })
  }
  const saveExerciseLog = (name, logType) => {
    const entry = exerciseInputs[name] || {}
    const clean = { type: logType.type }
    if (logType.type === 'time') {
      const duration = parseFloat(entry.duration)
      if (Number.isFinite(duration)) { clean.duration = duration; clean.unit = logType.unit }
    } else if (logType.type === 'rounds') {
      const rounds = parseFloat(entry.rounds)
      if (Number.isFinite(rounds)) clean.rounds = rounds
    } else {
      const weight = parseFloat(entry.weight)
      const reps = parseInt(entry.reps, 10)
      if (Number.isFinite(weight)) clean.weight = weight
      if (Number.isFinite(reps)) clean.reps = reps
    }
    const hasValue = Object.keys(clean).length > 1
    updateLog({
      exerciseLogs: {
        ...(log.exerciseLogs || {}),
        [name]: hasValue ? clean : deleteField(),
      },
    })
  }
  const clearExerciseLog = (name) => {
    setExerciseInputs((prev) => ({ ...prev, [name]: { weight: '', reps: '', duration: '', rounds: '' } }))
    updateLog({ exerciseLogs: { ...(log.exerciseLogs || {}), [name]: deleteField() } })
  }
  const toggleMeal = (key) => {
    const wasChecked = !!log.mealChecks?.[key]
    const next = { ...(log.mealChecks || {}), [key]: !wasChecked }
    updateLog({ mealChecks: next })
    // Consume pantry stock when a meal is checked off eaten; put it back if
    // the check is undone (e.g. a mistake). Uses the effective (possibly
    // custom) ingredients so edited meals deduct the right pantry items.
    const meal = effectiveMeals.find((m) => m.key === key)
    const sign = wasChecked ? 1 : -1
    for (const { key: itemKey, qty } of getEffectiveIngredients(meal || {})) {
      adjustStock(itemKey, sign * qty)
    }
  }
  const saveMealEdit = (key, items) => {
    updateLog({ customMeals: { ...(log.customMeals || {}), [key]: { items } } })
    setEditingMealKey(null)
  }
  const resetMealEdit = (key) => {
    updateLog({ customMeals: { ...(log.customMeals || {}), [key]: deleteField() } })
    setEditingMealKey(null)
  }
  const applyCustomMenu = (menu) => {
    const customMeals = {}
    for (const m of menu.meals) customMeals[m.key] = { items: m.items }
    updateLog({ customMeals })
  }
  const saveCurrentAsMenu = (name) => {
    const meals = effectiveMeals.map((m) => ({ key: m.key, time: m.time, label: m.label, items: getMealEditableItems(m) }))
    saveCustomMenu(name, meals)
  }
  const toggleSupplement = (key) => {
    const wasChecked = !!log.supplementChecks?.[key]
    const next = { ...(log.supplementChecks || {}), [key]: !wasChecked }
    updateLog({ supplementChecks: next })
    adjustStock(key, wasChecked ? 1 : -1)
  }

  const saveWeight = () => {
    const val = parseFloat(weightInput)
    updateLog({ weightKg: Number.isFinite(val) ? val : null })
  }
  const saveSteps = () => {
    const val = parseInt(stepsInput, 10)
    updateLog({ steps: Number.isFinite(val) ? val : null })
  }
  const saveNotes = () => updateLog({ notes: notesInput })
  const saveMeasurement = (field, value) => {
    const val = parseFloat(value)
    updateLog({ [field]: Number.isFinite(val) ? val : null })
  }
  const hasMeasurements = log.waistCm != null || log.chestCm != null || log.armsCm != null || log.thighsCm != null
  const measurementsExpanded = measurementsOpen || hasMeasurements

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setPhotoBusy(true)
    try {
      const dataUrl = await fileToCompressedDataUrl(file)
      await updateLog({ photo: dataUrl })
    } finally {
      setPhotoBusy(false)
    }
  }
  const removePhoto = () => updateLog({ photo: null })

  const enableAlerts = async () => {
    const perm = await requestNotificationPermission()
    const on = perm === 'granted'
    setAlertsOn(on)
    localStorage.setItem('alertsEnabled', String(on))
  }

  const exercisesDone = plan.exercises.filter((e) => log.exerciseChecks?.[e.name]).length
  const mealsDone = effectiveMeals.filter((m) => log.mealChecks?.[m.key]).length
  const suppsDone = plan.supplements.filter((s) => log.supplementChecks?.[s.key]).length
  const allExercisesDone = exercisesDone === plan.exercises.length
  const toggleAllExercises = () => {
    const next = {}
    plan.exercises.forEach((e) => { next[e.name] = !allExercisesDone })
    updateLog({ exerciseChecks: next })
  }

  const checkedKcal = effectiveMeals.filter((m) => log.mealChecks?.[m.key]).reduce((s, m) => s + m.kcal, 0)
  const checkedProtein = effectiveMeals.filter((m) => log.mealChecks?.[m.key]).reduce((s, m) => s + m.protein, 0)
  const dailyTotals = {
    kcal: effectiveMeals.reduce((s, m) => s + m.kcal, 0),
    protein: Math.round(effectiveMeals.reduce((s, m) => s + m.protein, 0) * 10) / 10,
  }

  const totalItems = plan.exercises.length + plan.meals.length + plan.supplements.length
  const doneItems = exercisesDone + mealsDone + suppsDone
  const dayPct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0
  const ringBg = `conic-gradient(var(--h-accent-dark) ${dayPct}%, rgba(14,14,14,.18) 0)`

  const stepsNum = parseInt(String(stepsInput).replace(/\D/g, ''), 10) || 0
  const stepsPct = Math.min(100, Math.round((stepsNum / plan.stepsTarget) * 100))

  const marqueeText = `${plan.workoutLabel.toUpperCase()}  /  ${dailyTotals.kcal} KCAL  /  ${dailyTotals.protein}G PROTEIN  /  ${plan.stepsTarget.toLocaleString()} STEPS  /  PHASE ${String(plan.phase.id).padStart(2, '0')} ${plan.phase.name.toUpperCase()}`

  const tomorrowPlan = hasTomorrow ? getEffectiveDayPlan(tomorrowDayNumber, aiPlan) : null
  const tomorrowEffectiveMeals = tomorrowPlan ? getEffectiveMeals(tomorrowPlan.meals, tomorrowLog?.customMeals) : []
  const tomorrowIngredients = tomorrowPlan ? getEffectiveGroceryList(tomorrowEffectiveMeals) : []
  const tomorrowNeeded = tomorrowIngredients.filter((g) => (inventory[g.key]?.qty || 0) < g.qty)

  return (
    <div>
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="day-eyebrow-row">
          <span className="day-eyebrow">
            {isToday && <span className="day-eyebrow-dot" />}
            {isToday ? `TODAY · ${formatDayDate(dayNumber)}` : `DAY ${String(dayNumber).padStart(2, '0')} · ${formatDayDate(dayNumber)}`}
          </span>
          <span className="phase-pill">PHASE {plan.phase.id} · WEEK {plan.week}</span>
        </div>
        <h1 className="display-heading">{plan.phase.name}</h1>
        <div className="display-heading-outline">Day {String(dayNumber).padStart(2, '0')}</div>
        <p className="display-sub">{plan.phase.split} · {plan.stepsTarget.toLocaleString()} steps/day</p>

        <div className="stat-row">
          <div className="stat-hero">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="stat-hero-label">{isToday ? 'TODAY' : 'DAY'}</span>
              <span className="stat-hero-value">{dayPct}%</span>
            </div>
            <div className="stat-ring" style={{ background: ringBg }}>
              <div className="stat-ring-dot" />
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-card-label">WORKOUT</span>
            <span className="stat-card-value">{exercisesDone}<span className="stat-card-of"> / {plan.exercises.length}</span></span>
          </div>
          <div className="stat-card">
            <span className="stat-card-label">MEALS</span>
            <span className="stat-card-value">{mealsDone}<span className="stat-card-of"> / {plan.meals.length}</span></span>
          </div>
          <div className="stat-card">
            <span className="stat-card-label">PROTEIN</span>
            <span className="stat-card-value">{checkedProtein}<span className="stat-card-of"> / {dailyTotals.protein}g</span></span>
          </div>
        </div>
      </section>

      <Marquee text={marqueeText} />

      <section className="section">
        {isToday && notificationsSupported() && !alertsOn && (
          <div className="reminder-banner">
            <div className="reminder-banner-left">
              <span className="reminder-dot" />
              <span className="reminder-text">Turn on meal &amp; supplement reminders</span>
            </div>
            <button className="reminder-btn" onClick={enableAlerts}>ENABLE</button>
          </div>
        )}

        <div className="card-grid">
          {/* Workout */}
          <div className="card">
            <div className="card-head">
              <div className="card-title-group">
                <span className="card-index">01</span>
                <h2 className="card-title">{plan.workoutLabel}</h2>
              </div>
              <span className="card-count">{exercisesDone}/{plan.exercises.length}</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${(exercisesDone / plan.exercises.length) * 100}%` }} />
            </div>
            {plan.exercises.map((e) => {
              const pr = exercisePRs[e.name]
              const logType = getExerciseLogType(e.range)
              const inputs = exerciseInputs[e.name] || { weight: '', reps: '', duration: '', rounds: '' }
              const setField = (field) => (ev) =>
                setExerciseInputs((prev) => ({ ...prev, [e.name]: { ...prev[e.name], [field]: ev.target.value } }))
              const save = () => saveExerciseLog(e.name, logType)
              return (
                <div key={e.name} className="check-row-wrap">
                  <CheckRow
                    checked={log.exerciseChecks?.[e.name]}
                    onToggle={() => toggleExercise(e.name)}
                    title={e.name}
                    subtitle={`${e.sets} SETS × ${e.range}${e.note ? ' · ' + e.note : ''}`}
                    watchUrl={e.watchUrl}
                    onHistory={() => setHistoryExerciseName(historyExerciseName === e.name ? null : e.name)}
                  />
                  {historyExerciseName === e.name && (
                    <ExerciseHistoryChart exerciseName={e.name} logs={allLogs} onClose={() => setHistoryExerciseName(null)} />
                  )}
                  <div className="exercise-log-row">
                    {logType.type === 'weight-reps' && (
                      <>
                        <input type="number" inputMode="decimal" className="exercise-log-input" placeholder="KG" value={inputs.weight} onChange={setField('weight')} onBlur={save} />
                        <span className="exercise-log-x">×</span>
                        <input type="number" inputMode="numeric" className="exercise-log-input" placeholder="REPS" value={inputs.reps} onChange={setField('reps')} onBlur={save} />
                      </>
                    )}
                    {logType.type === 'time' && (
                      <input
                        type="number"
                        inputMode="decimal"
                        className="exercise-log-input exercise-log-input-wide"
                        placeholder={logType.unit === 'sec' ? 'SEC' : 'MIN'}
                        value={inputs.duration}
                        onChange={setField('duration')}
                        onBlur={save}
                      />
                    )}
                    {logType.type === 'rounds' && (
                      <input
                        type="number"
                        inputMode="numeric"
                        className="exercise-log-input exercise-log-input-wide"
                        placeholder="ROUNDS"
                        value={inputs.rounds}
                        onChange={setField('rounds')}
                        onBlur={save}
                      />
                    )}
                    {log.exerciseLogs?.[e.name] && (
                      <button className="exercise-log-clear" onClick={() => clearExerciseLog(e.name)} type="button" aria-label="Clear">×</button>
                    )}
                    {pr && <span className="exercise-pr-badge">BEST {formatExerciseLog(pr)}</span>}
                  </div>
                </div>
              )
            })}
            <button className={`master-check ${allExercisesDone ? 'is-complete' : ''}`} onClick={toggleAllExercises}>
              {allExercisesDone ? '✓ DAY COMPLETE' : 'MARK WHOLE DAY COMPLETE'}
            </button>
          </div>

          {/* Diet */}
          <div className="card">
            <div className="card-head">
              <div className="card-title-group">
                <span className="card-index">02</span>
                <h2 className="card-title">Diet</h2>
              </div>
              <span className="card-count">{mealsDone}/{plan.meals.length}</span>
            </div>
            <p className="card-subtitle">Menu {plan.menuNumber} · {plan.menuName}</p>
            <CustomMenuBar menus={customMenus} onApply={applyCustomMenu} onSaveCurrent={saveCurrentAsMenu} onDelete={deleteCustomMenu} />
            <div className="mini-stats">
              <div className="mini-stat">
                <div className="mini-stat-top"><span>KCAL</span><span>{checkedKcal} / {dailyTotals.kcal}</span></div>
                <div className="mini-progress-track">
                  <div className="mini-progress-fill" style={{ width: `${Math.min(100, (checkedKcal / dailyTotals.kcal) * 100)}%`, background: 'var(--h-text)' }} />
                </div>
              </div>
              <div className="mini-stat">
                <div className="mini-stat-top"><span>PROTEIN</span><span>{checkedProtein} / {dailyTotals.protein}g</span></div>
                <div className="mini-progress-track">
                  <div className="mini-progress-fill" style={{ width: `${Math.min(100, (checkedProtein / dailyTotals.protein) * 100)}%`, background: 'var(--h-accent)' }} />
                </div>
              </div>
            </div>
            {effectiveMeals.map((m) => (
              <div key={m.key} className="check-row-wrap">
                <CheckRow
                  checked={log.mealChecks?.[m.key]}
                  onToggle={() => toggleMeal(m.key)}
                  time={m.time}
                  title={m.label}
                  tag={`${m.kcal} kcal · ${m.protein}g P`}
                  desc={m.items.join(', ')}
                  isCustom={m.isCustom}
                  onEdit={() => setEditingMealKey(editingMealKey === m.key ? null : m.key)}
                />
                {editingMealKey === m.key && (
                  <MealEditor
                    items={getMealEditableItems(m)}
                    isCustom={m.isCustom}
                    onSave={(items) => saveMealEdit(m.key, items)}
                    onReset={() => resetMealEdit(m.key)}
                    onClose={() => setEditingMealKey(null)}
                  />
                )}
              </div>
            ))}
            <p className="card-subtitle">Daily total ≈ {dailyTotals.kcal} kcal · {dailyTotals.protein}g protein</p>
          </div>

          {/* Supplements */}
          <div className="card">
            <div className="card-head">
              <div className="card-title-group">
                <span className="card-index">03</span>
                <h2 className="card-title">Supplements</h2>
              </div>
              <span className="card-count">{suppsDone}/{plan.supplements.length}</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${(suppsDone / plan.supplements.length) * 100}%` }} />
            </div>
            {plan.supplements.map((s) => (
              <CheckRow
                key={s.key}
                checked={log.supplementChecks?.[s.key]}
                onToggle={() => toggleSupplement(s.key)}
                time={s.time}
                title={s.name}
                tag={s.dose}
                desc={s.note}
              />
            ))}
          </div>

          {/* Daily log */}
          <div className="card">
            <div className="card-title-group" style={{ marginBottom: 24 }}>
              <span className="card-index">04</span>
              <h2 className="card-title">Daily Log</h2>
            </div>
            <div className="log-grid">
              <label className="log-field">
                <span className="log-field-label">BODY WEIGHT</span>
                <div className="log-input-wrap">
                  <input
                    inputMode="decimal"
                    placeholder="00.0"
                    value={weightInput}
                    onChange={(e) => setWeightInput(e.target.value)}
                    onBlur={saveWeight}
                  />
                  <span className="log-input-suffix">KG</span>
                </div>
              </label>
              <label className="log-field">
                <span className="log-field-label"><span>STEPS</span><span style={{ color: 'var(--h-accent)' }}>TARGET {plan.stepsTarget.toLocaleString()}</span></span>
                <div className="log-input-wrap">
                  <input
                    inputMode="numeric"
                    placeholder="0"
                    value={stepsInput}
                    onChange={(e) => setStepsInput(e.target.value)}
                    onBlur={saveSteps}
                  />
                  <span className="log-input-suffix">{stepsPct}%</span>
                </div>
              </label>
            </div>
            <div className="progress-track" style={{ marginTop: -6, marginBottom: 0 }}>
              <div className="progress-fill" style={{ width: `${stepsPct}%`, height: '6px' }} />
            </div>
            <p className="card-subtitle" style={{ marginTop: 16 }}>Weigh in same morning, empty stomach. Track the weekly average, not daily noise.</p>

            {measurementsExpanded ? (
              <div style={{ marginTop: 16 }}>
                <span className="log-field-label">BODY MEASUREMENTS (CM)</span>
                <div className="log-grid" style={{ marginTop: 10 }}>
                  <label className="log-field">
                    <span className="log-field-label">WAIST</span>
                    <div className="log-input-wrap">
                      <input inputMode="decimal" placeholder="00.0" value={waistInput} onChange={(e) => setWaistInput(e.target.value)} onBlur={() => saveMeasurement('waistCm', waistInput)} />
                      <span className="log-input-suffix">CM</span>
                    </div>
                  </label>
                  <label className="log-field">
                    <span className="log-field-label">CHEST</span>
                    <div className="log-input-wrap">
                      <input inputMode="decimal" placeholder="00.0" value={chestInput} onChange={(e) => setChestInput(e.target.value)} onBlur={() => saveMeasurement('chestCm', chestInput)} />
                      <span className="log-input-suffix">CM</span>
                    </div>
                  </label>
                  <label className="log-field">
                    <span className="log-field-label">ARMS</span>
                    <div className="log-input-wrap">
                      <input inputMode="decimal" placeholder="00.0" value={armsInput} onChange={(e) => setArmsInput(e.target.value)} onBlur={() => saveMeasurement('armsCm', armsInput)} />
                      <span className="log-input-suffix">CM</span>
                    </div>
                  </label>
                  <label className="log-field">
                    <span className="log-field-label">THIGHS</span>
                    <div className="log-input-wrap">
                      <input inputMode="decimal" placeholder="00.0" value={thighsInput} onChange={(e) => setThighsInput(e.target.value)} onBlur={() => saveMeasurement('thighsCm', thighsInput)} />
                      <span className="log-input-suffix">CM</span>
                    </div>
                  </label>
                </div>
              </div>
            ) : (
              <button type="button" className="btn btn-ghost full-width" style={{ marginTop: 16 }} onClick={() => setMeasurementsOpen(true)}>
                + ADD BODY MEASUREMENTS
              </button>
            )}

            <label className="log-field" style={{ marginTop: 8 }}>
              <span className="log-field-label">NOTES</span>
              <textarea
                rows={4}
                placeholder="How did it feel? Anything to remember..."
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                onBlur={saveNotes}
              />
            </label>

            <div className="log-field" style={{ marginTop: 8 }}>
              <span className="log-field-label">PROGRESS PHOTO</span>
              {log.photo ? (
                <div className="photo-preview">
                  <img src={log.photo} alt={`Day ${dayNumber}`} />
                  <button className="btn btn-ghost" onClick={removePhoto} type="button">REMOVE</button>
                </div>
              ) : (
                <label className="btn btn-ghost full-width photo-upload-btn">
                  {photoBusy ? 'PROCESSING…' : '+ ADD PHOTO'}
                  <input type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} disabled={photoBusy} />
                </label>
              )}
            </div>
          </div>

          {/* Tomorrow preview */}
          {tomorrowPlan && (
            <div className="card">
              <div className="card-head">
                <div className="card-title-group">
                  <span className="card-index">05</span>
                  <h2 className="card-title">Tomorrow</h2>
                </div>
                <span className="card-count">{tomorrowNeeded.length ? `${tomorrowNeeded.length} TO BUY` : 'ALL SET'}</span>
              </div>
              <p className="card-subtitle">{formatDayDate(tomorrowDayNumber)} · {tomorrowPlan.workoutLabel} · Menu {tomorrowPlan.menuNumber} · {tomorrowPlan.menuName}</p>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tomorrowEffectiveMeals.map((m) => (
                  <div key={m.key} style={{ display: 'flex', gap: 10, fontSize: 14 }}>
                    <span className="mono" style={{ color: 'var(--h-accent)', minWidth: 44 }}>{m.time}</span>
                    <span className="muted">{m.items.join(', ')}{m.isCustom ? ' (edited)' : ''}</span>
                  </div>
                ))}
              </div>
              <Link to="/groceries" className="btn btn-ghost full-width" style={{ marginTop: 18, display: 'block' }}>
                {tomorrowNeeded.length ? `VIEW SHOPPING LIST (${tomorrowNeeded.length})` : 'VIEW GROCERIES'}
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
